import * as THREE from 'three'

export default class Core {
  sunLight = new THREE.DirectionalLight(0xfff4d2, 1.75)
  skyLight = new THREE.HemisphereLight(0xb8dcff, 0x31451f, 0.95)
  bounceLight = new THREE.DirectionalLight(0x9ec07e, 0.35)
  moonLight = new THREE.DirectionalLight(0x9bbcff, 0.35)
  dayColor = new THREE.Color(0x87ceeb)
  duskColor = new THREE.Color(0xf0a15c)
  nightColor = new THREE.Color(0x071126)
  skyColorScratch = new THREE.Color()
  timeOfDay = 100
  secondsPerDay = 1200

  constructor() {
    this.camera = new THREE.PerspectiveCamera()
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
      stencil: false
    })
    this.scene = new THREE.Scene()
    this.initScene()
    this.initRenderer()
    this.initCamera()
  }

  camera: THREE.PerspectiveCamera
  scene: THREE.Scene
  renderer: THREE.WebGLRenderer

  get daylight() {
    return THREE.MathUtils.smoothstep(
      Math.max(0, Math.sin(this.timeOfDay * Math.PI * 2)),
      0,
      1
    )
  }

  get isNight() {
    return this.timeOfDay >= 0.542 && this.timeOfDay < 0.958
  }

  getTimeLabel() {
    if (this.timeOfDay >= 0.917) return 'Dawn'
    if (this.timeOfDay < 0.5) return 'Day'
    if (this.timeOfDay < 0.542) return 'Dusk'
    return 'Night'
  }

  getClockLabel() {
    const hour = Math.floor((this.timeOfDay * 24 + 6) % 24)
    const minute = Math.floor((this.timeOfDay * 24 * 60) % 60)
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  }

  getTimeHudText() {
    return `${this.getTimeLabel()} ${this.getClockLabel()}`
  }

  dimension: 'overworld' | 'nether' = 'overworld'

  applyDimensionEnvironment(dimension: 'overworld' | 'nether') {
    this.dimension = dimension
    if (dimension === 'nether') {
      this.applyNetherEnvironment()
    } else {
      this.applyDayNight()
    }
  }

  applyNetherEnvironment() {
    const skyColor = new THREE.Color(0x1a0303)
    if (this.scene.background instanceof THREE.Color) {
      this.scene.background.copy(skyColor)
    } else {
      this.scene.background = skyColor.clone()
    }
    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.color.copy(skyColor)
      this.scene.fog.near = 5
      this.scene.fog.far = 45
    }
    this.sunLight.intensity = 0
    this.moonLight.intensity = 0
    this.skyLight.color.setHex(0x601010)
    this.skyLight.groundColor.setHex(0x1a0505)
    this.skyLight.intensity = 0.8
    this.bounceLight.color.setHex(0xff3300)
    this.bounceLight.intensity = 0.6
    this.renderer.toneMappingExposure = 0.6
  }

  updateDayNight(delta: number, active: boolean) {
    if (this.dimension === 'nether') {
      this.applyNetherEnvironment()
      return
    }
    if (active) {
      this.timeOfDay = (this.timeOfDay + delta / this.secondsPerDay) % 1
    }
    this.applyDayNight()
  }

  applyDayNight() {
    const daylight = this.daylight
    const night = 1 - daylight
    const dusk =
      Math.max(0, 1 - Math.abs(this.timeOfDay - 0.5) / 0.08) +
      Math.max(0, 1 - Math.min(this.timeOfDay, 1 - this.timeOfDay) / 0.06)

    const skyColor = this.skyColorScratch.lerpColors(
      this.nightColor,
      this.dayColor,
      daylight
    )
    skyColor.lerp(this.duskColor, Math.min(0.35, dusk * 0.24))

    if (this.scene.background instanceof THREE.Color) {
      this.scene.background.copy(skyColor)
    } else {
      this.scene.background = skyColor.clone()
    }

    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.color.copy(skyColor)
      this.scene.fog.near = THREE.MathUtils.lerp(10, 18, daylight)
      this.scene.fog.far = THREE.MathUtils.lerp(95, 180, daylight)
    }

    const sunAngle = this.timeOfDay * Math.PI * 2
    this.sunLight.position.set(
      Math.cos(sunAngle) * 180,
      Math.max(12, Math.sin(sunAngle) * 260),
      140
    )
    this.moonLight.position.set(
      -Math.cos(sunAngle) * 140,
      Math.max(30, -Math.sin(sunAngle) * 180),
      -120
    )
    this.sunLight.intensity = 0.08 + daylight * 1.75
    this.skyLight.intensity = 0.22 + daylight * 0.82
    this.bounceLight.intensity = daylight * 0.35
    this.moonLight.intensity = night * 0.55
    this.renderer.toneMappingExposure = 0.52 + daylight * 0.43
  }

  initCamera = () => {
    this.camera.fov = 50
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.near = 0.01
    this.camera.far = 500
    this.camera.updateProjectionMatrix()
    this.camera.position.set(8, 50, 8)

    this.camera.lookAt(100, 30, 100)

    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight
      this.camera.updateProjectionMatrix()
    })
  }

  initScene = () => {
    this.scene = new THREE.Scene()
    const backgroundColor = 0x87ceeb

    this.scene.fog = new THREE.Fog(backgroundColor, 18, 180)
    this.scene.background = new THREE.Color(backgroundColor)

    this.sunLight.position.set(180, 260, 140)
    this.scene.add(this.sunLight)

    this.scene.add(this.skyLight)

    this.bounceLight.position.set(-80, 60, -120)
    this.scene.add(this.bounceLight)

    this.moonLight.position.set(-140, 180, -120)
    this.scene.add(this.moonLight)
    this.applyDayNight()
  }

  initRenderer = () => {
    this.renderer.outputEncoding = THREE.sRGBEncoding
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 0.95
    this.renderer.physicallyCorrectLights = true
    this.renderer.shadowMap.enabled = false
    this.renderer.setPixelRatio(1)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    document.body.appendChild(this.renderer.domElement)

    window.addEventListener('resize', () => {
      this.renderer.setSize(window.innerWidth, window.innerHeight)
    })
  }
}
